import * as qs from "qs";
import { emptyApi } from "./emptyApi";
import { Pagination } from "types/Models/pagination";
import { Group } from "types/Models/group";
import { ArrayElement } from "types/utils/ArrayElement";
import { makeFormData } from "utils/makeFormData";
import {
  BroadcastCallFilterAll,
  BroadcastCallFilterUpcoming,
  BroadcastCallResponse,
  BroadcastResponse,
  SlotValidationResponse,
} from "types/Models/broadcastCall";

export type BroadcastchannelResponse = {
  url: string;
  name: string;
  group_name: string;
  season: string;
  uuid: string;
  company_entity?: Pick<
    ArrayElement<Group["entities"]>,
    "uuid" | "name" | "country_code"
  >;
};

type Filter = {
  channel_types: string;
  task_status_ids: string;
  seasons: string;
  subscription_types: string;
  agent_ids: string;
  group_ids: string;
};

export type OpsCRMFilters = {
  page_num?: number;
  search_term?: string | null;
  crm_type: string | null;
  subscription_type?: string | null;
  agent_filter?: string | null;
  sales_person_filter?: string | null;
  us_state_uuids?: string | null;
  us_incorp_months?: string | null;
  tag_filter?: string | null;
  task_state_filter_uuids?: string | null;
  not_launched_task_filter?: string | null;
  active_subscribers?: string | null;
  base_task_template_id?: string | null;
  task_season?: string;
  sort_order?: string | null;
  sort_by?: string | null;
  generate_report?: boolean;
  archived?: boolean;
  service_team_id?: string;
};

export type BroadcastTemplateResponse = {
  uuid: string;
  template_id: string;
  template_body: string;
  template_display_name: string;
  template_subject: string;
  template_variables: Record<
    string,
    {
      display_name: string;
      is_whatsapp_button: boolean;
    }
  >;
}[];

export type TemplateTypes = "chat" | "whatsapp" | "email";

const extendedApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getBroadcastChannelsForCall: build.query<
      Pagination & { channels: BroadcastchannelResponse[] },
      {
        page_num: number;
        broadcast_csv?: File | null;
      }
    >({
      query: ({ broadcast_csv, ...queryParams }) => {
        const query = qs.stringify(queryParams, { addQueryPrefix: true });
        return {
          url: `/api/inkle/messaging/broadcast/file/upload/channel-list/${query}`,
          body: makeFormData({ broadcast_csv }),
          method: "POST",
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.channels.map(
                ({ url }) => ({ type: "ChannelListQuery", id: "NO" } as const)
              ),
            ]
          : [],
    }),
    validateCSV: build.mutation<
      BroadcastCallResponse,
      {
        csv_file?: File | null;
        page?: string;
      }
    >({
      query: ({ ...props }) => {
        return {
          url: `/api/inkle/messaging/bulk-calls/csv-validation/`,
          method: "POST",
          body: makeFormData({
            ...props,
          }),
        };
      },
    }),
    getBroadcastList: build.query<
      BroadcastResponse,
      {
        page: number;
        filter:
          | typeof BroadcastCallFilterUpcoming
          | typeof BroadcastCallFilterAll;
        broadcast_types: string | null;
        status: string | null;
        scheduled_date_from: string | null;
        scheduled_date_to: string | null;
        search_term: string | null;
        ivr_status: string | null;
      }
    >({
      query: (query) => {
        let queryUrl = qs.stringify(query, {
          skipNulls: true,
          addQueryPrefix: true,
        });
        return {
          url: `/api/inkle/messaging/unified-broadcast/${queryUrl}`,
        };
      },
      providesTags: ["BROADCAST_LIST"],
    }),

    cancelBroadcastCall: build.mutation<
      void,
      {
        campaign_uuid: string;
      }
    >({
      query: ({ campaign_uuid }) => {
        return {
          url: `/api/inkle/messaging/bulk-calls/campaigns/cancel/`,
          method: "post",
          body: { campaign_uuid },
        };
      },
      invalidatesTags: ["BROADCAST_LIST"],
    }),
    csvTimeEstimationAndValidate: build.mutation<
      SlotValidationResponse,
      {
        message_text: string;
        csv_file: File | null;
        scheduled_date: string;
        start_time: string;
      }
    >({
      query: ({ ...props }) => {
        return {
          url: `/api/inkle/messaging/bulk-calls/csv-time-estimation/`,
          method: "post",
          body: makeFormData({
            ...props,
          }),
        };
      },
    }),
    createBroadcastCalls: build.mutation<
      void,
      {
        campaign_name: string;
        message_text: string;
        csv_file: File | null;
        scheduled_date: string;
        scheduled_time_start: string;
        scheduled_time_end: string;
      }
    >({
      query: ({ ...props }) => {
        return {
          url: `/api/inkle/messaging/bulk-calls/campaigns/create/`,
          method: "post",
          body: makeFormData({
            ...props,
          }),
        };
      },
      invalidatesTags: ["BROADCAST_LIST"],
    }),
    testIVRCall: build.mutation<
      void,
      {
        team_member_name: string;
        company_name: string;
        entity_name: string;
        phone_number: string;
        message_text: string;
      }
    >({
      query: (payload) => {
        return {
          url: `/api/inkle/messaging/bulk-calls/test-ivr-call/`,
          method: "POST",
          body: payload,
        };
      },
    }),
    exportIVRCall: build.mutation<
      { download_url: string },
      {
        campaign_uuids: string;
        generate_report: boolean;
        export_to_email: boolean;
      }
    >({
      query: (payload) => {
        return {
          url: `/api/inkle/messaging/bulk-calls/campaigns/export/`,
          method: "POST",
          body: payload,
        };
      },
    }),
  }),
});

export const {
  useGetBroadcastChannelsForCallQuery,
  useLazyGetBroadcastChannelsForCallQuery,
  useValidateCSVMutation,
  useGetBroadcastListQuery,
  useCancelBroadcastCallMutation,
  useCsvTimeEstimationAndValidateMutation,
  useCreateBroadcastCallsMutation,
  useTestIVRCallMutation,
  useExportIVRCallMutation,
} = extendedApi;
